import { cleanVariantGID, cleanProductGID } from '@/utils/cleanGIDs';
import elevarCartProductList from './cart-item-list';
import { cleanCustomerGID } from '@/utils/cleanGIDs';
import { getPersonalizeExperiences } from '@/utils/events/gtmEvents.js';

const findProductType = (attributes) => {
  if (!Array.isArray(attributes)) return null;
  const productType = attributes.find((attr) => attr?.key === '_productType');
  return productType?.value ?? null;
};

export function getUserData() {
  const custdata = JSON.parse(window?.localStorage?.getItem('userdata'));
  let elevarUser = {};

  if (custdata) {
    elevarUser = {
      visitor_type: 'logged_in',
      customer_id: cleanCustomerGID(custdata.id),
      customer_email: custdata?.emailAddress?.emailAddress
    };
  } else {
    elevarUser = {
      visitor_type: 'guest'
    };
  }

  return elevarUser;
}

export async function elevarBaseData(cart) {
  const cartLines = cart?.lines || [];
  /** Elevar Base Data Layer **/
  // Should be fired before all other events and on virtual page change

  const findProductImage = (attributes) => {
    const productImage = attributes.find(
      (attr) => attr.key === '_productImage'
    );
    return productImage ? productImage.value : '';
  };

  const products = [];

  cartLines.forEach((cartItem, index) => {
    const itemProductTypeGpc = findProductType(cartItem?.attributes);

    const product = {
      id: cartItem?.merchandise?.sku || '', // SKU
      name: cartItem?.merchandise?.product?.title || '', // Product title
      brand: 'Leesa',
      category: cartItem?.merchandise?.product?.productType || '',
      variant: cartItem?.merchandise?.title || '',
      price: cartItem?.merchandise?.price.amount || '0.0',
      list:
        elevarCartProductList.find((item) => item.type === itemProductTypeGpc)
          ?.list || null, // The list the product was discovered from
      product_id: String(cleanProductGID(cartItem?.merchandise?.product?.id)),
      variant_id: String(cleanVariantGID(cartItem?.merchandise?.id)),
      quantity: String(cartItem?.quantity ?? 1),
      compare_at_price: cartItem?.merchandise?.compareAtPrice.amount || '0.0',
      image: findProductImage(cartItem?.attributes),
      position: index + 1
    };

    products.push(product);
  });

  const userdata = getUserData();
  const personalizeData = getPersonalizeExperiences();

  // check existing window.ElevarDataLayer to see if the same data exists first.
  // if the same data does not exist then push the new data.
  const newEvent = {
    event: 'dl_user_data',
    cart_total: String(cart?.cost?.subtotalAmount?.amount ?? '0.0'),
    user_properties: userdata,
    lsa_personalize: {
      experiences: personalizeData?.map((exp) => ({
        experienceUid: exp.shortUid,
        variantUid: exp.activeVariantShortUid
      }))
    },
    ecommerce: {
      currencyCode: 'USD',
      cart_contents: {
        products: products // See the products array below
      }
    }
  };

  function waitForElevarDataLayer() {
    return new Promise((resolve) => {
      if (window.ElevarDataLayer) {
        return resolve(window.ElevarDataLayer);
      }

      const interval = setInterval(() => {
        if (window.ElevarDataLayer) {
          clearInterval(interval);
          resolve(window.ElevarDataLayer);
        }
      }, 100);
    });
  }

  await waitForElevarDataLayer().then(() => {
    window.ElevarDataLayer.push(newEvent);
  });
}

export async function elevarViewCart(cart) {
  if (typeof window === 'undefined') return;
  const cartLines = cart?.lines || [];
  const products = [];

  cartLines.forEach((cartItem, index) => {
    const product = {
      id: cartItem?.merchandise?.sku || '', // SKU
      name: cartItem?.merchandise?.product?.title || '', // Product title
      brand: 'Leesa',
      category: cartItem?.merchandise?.product?.productType || '',
      variant: cartItem?.merchandise?.title || '',
      price: cartItem?.merchandise?.price.amount || '0.0',
      list: '', // We do not store the the list it was discovered on.
      product_id: String(cleanProductGID(cartItem?.merchandise?.product?.id)),
      variant_id: String(cleanVariantGID(cartItem?.merchandise?.id)),
      quantity: String(cartItem?.quantity) || String(1),
      compare_at_price: cartItem?.merchandise?.compareAtPrice.amount || '0.0',
      image: cartItem?.merchandise?.image?.url || '',
      position: index + 1
    };

    // we limit the objects in the array to 4 due to a character limit in the Elevar dataLayer.
    if (products.length < 4) {
      products.push(product);
    }
  });
  const userdata = getUserData();

  if (window.ElevarDataLayer) {
    window.ElevarDataLayer.push({
      event: 'dl_view_cart',
      user_properties: userdata,
      cart_total: String(cart?.cost?.subtotalAmount?.amount ?? '0.0'),
      ecommerce: {
        currencyCode: 'USD',
        actionField: {
          list: 'Shopping Cart'
        },
        impressions: products // See impressions array below
      }
    });
  }
}

export async function elevarAddToCart(cartPayloadQueue, quantity) {
  // Note: quantity prop comes from if the visitor clicks on quantity increment in the cart and it increases the quantity by 1.
  if (typeof window === 'undefined') return;
  const storedReferrer = sessionStorage.getItem('referringCollection');

  const products = [];

  // Note: used storedReferrer because the docs say the list action field should be the collections url the product was found on but i've
  // pulled in the pathname of the product url too as i assume it should actually be the pathname!!! we will wait and find out from Elevar.

  const findProductImage = (attributes) => {
    const productImage = attributes.find(
      (attr) => attr.key === '_productImage'
    );
    return productImage ? productImage.value : '';
  };

  if (quantity) {
    cartPayloadQueue.forEach((cartItem) => {
      const product = {
        id: cartItem?.merchandise?.sku || '', // SKU
        name: cartItem?.merchandise?.product?.title || '', // Product title
        brand: 'Leesa',
        category: cartItem?.merchandise?.product?.productType || '',
        variant: cartItem?.merchandise?.title || '',
        price: cartItem?.merchandise?.price.amount || '0.0',
        list: storedReferrer || '', // The list the product was discovered from
        product_id: String(cleanProductGID(cartItem?.merchandise?.product?.id)),
        product_type: cartItem?.merchandise?.product?.productType || '',
        product_handle: cartItem?.merchandise?.product?.handle || '',
        variant_id: String(cleanVariantGID(cartItem?.merchandise?.id)),
        quantity: String(quantity),
        compare_at_price: cartItem?.merchandise.compareAtPrice.amount || '0.0',
        image: findProductImage(cartItem?.attributes)
      };
      products.push(product);
    });
  } else {
    cartPayloadQueue.forEach((cartItem) => {
      const product = {
        id: cartItem?.variant?.sku || '', // SKU
        name: cartItem?.product?.title || '', // Product title
        brand: 'Leesa',
        category: cartItem?.product?.productType || '',
        variant: cartItem?.variant?.title || '',
        price: cartItem?.variant?.price?.amount || '0.0',
        list: storedReferrer || '', // The list the product was discovered from
        product_id: String(
          cleanProductGID(
            cartItem?.product?.id || cartItem?.product?.shopifyProduct?.id
          )
        ),
        product_type: cartItem?.product?.productType || '',
        product_handle: cartItem?.product?.handle || '',
        variant_id: String(cleanVariantGID(cartItem?.variant?.id)),
        quantity: String(cartItem?.quantity) || String(1),
        compare_at_price: cartItem?.variant?.compareAtPrice?.amount || '0.0',
        image: findProductImage(cartItem?.customAttributes)
      };
      products.push(product);
    });
  }
  const userdata = getUserData();
  if (window.ElevarDataLayer) {
    window.ElevarDataLayer.push({
      ecommerce: null
    });

    window.ElevarDataLayer.push({
      event: 'dl_add_to_cart',
      user_properties: userdata,
      ecommerce: {
        currencyCode: 'USD',
        add: {
          actionField: {
            list: storedReferrer || '' // This should be the product url path
          },
          products: products // See the products array below
        }
      }
    });
  }
}

export async function elevarRemoveFromCart(productsToRemove, quantity) {
  // Note: quantity prop comes from if the visitor clicks on quantity decrement in the cart and it reduces the quantity by 1.

  if (typeof window === 'undefined') return;
  const storedReferrer = sessionStorage.getItem('referringCollection');

  const findProductImage = (attributes) => {
    const productImage = attributes.find(
      (attr) => attr.key === '_productImage'
    );
    return productImage ? productImage.value : '';
  };

  const products = [];

  productsToRemove.forEach((removedItem) => {
    const product = {
      id: removedItem?.merchandise?.sku || '', // SKU
      name: removedItem?.merchandise?.product?.title || '', // Product title
      brand: 'Leesa',
      category: removedItem?.merchandise?.product?.productType || '',
      variant: removedItem?.merchandise.title || '',
      price: removedItem?.merchandise.price?.amount || '0.0',
      list: '', // The list the product was discovered from / Note not possible on a remove event in the cart page. we don't hold this info once the product is in cart.
      product_id: String(cleanProductGID(removedItem?.merchandise.product?.id)),
      variant_id: String(cleanVariantGID(removedItem?.merchandise?.id)),
      quantity:
        quantity == 1
          ? String(quantity)
          : String(removedItem?.quantity) || String(1),
      compare_at_price:
        removedItem?.merchandise?.compareAtPrice?.amount || '0.0',
      image: findProductImage(removedItem?.attributes) || ''
    };
    products.push(product);
  });

  const userdata = getUserData();

  if (window.ElevarDataLayer) {
    window.ElevarDataLayer.push({
      event: 'dl_remove_from_cart',
      user_properties: userdata,
      ecommerce: {
        currencyCode: 'USD',
        remove: {
          actionField: {
            list: storedReferrer || '' // this should be the collection page URL that user clicked product from
          },
          products: products // See the products array below
        }
      }
    });
  }
}
